import { z } from 'zod'

/**
 * Environment variables, validated once at module load.
 *
 * Vite inlines anything prefixed `VITE_` into the bundle at build time, so
 * **every value here ships to the browser**. A secret must never gain that
 * prefix — there is no such thing as a private `VITE_` variable.
 *
 * Validating here rather than reading `import.meta.env` at each call site
 * means a missing variable fails immediately and by name, instead of becoming
 * the string "undefined" inside a URL and surfacing as a confusing 404 three
 * screens away from the cause.
 */
const schema = z.object({
  /** Base URL of the VentaWiz API. Empty means same-origin. */
  VITE_API_BASE_URL: z.string().default(''),
  /**
   * Base URL of VentaWiz-Auth. Same-origin by default, and it needs to stay
   * that way: the refresh token is a `__Host-` cookie, which the browser
   * sends only to the exact host that set it. A cross-origin auth service
   * cannot receive it at all — and Safari's ITP would block it as a
   * third-party cookie even without the prefix. Hence the proxy.
   */
  VITE_AUTH_BASE_URL: z.string().default('/auth'),
  /**
   * The shared parent domain for the `vw_consent` cookie (e.g. `ventawiz.io`),
   * when the apps are on different subdomains. Empty means a host-only cookie —
   * right for one origin, and locally, where every dev server is `localhost`
   * and cookies ignore the port.
   */
  VITE_CONSENT_COOKIE_DOMAIN: z.string().default(''),
  /** Build-time mode, supplied by Vite. */
  MODE: z.string().default('development'),
})

export type Env = {
  apiBaseUrl: string
  authBaseUrl: string
  consentCookieDomain: string | undefined
  mode: string
  isProduction: boolean
}

/**
 * Parse and validate a raw environment object.
 *
 * Exported separately from the module-level {@link env} so the failure path is
 * testable. A `throw` at import time cannot be asserted on without tearing the
 * module registry apart, and an untested failure path is how a misconfiguration
 * guard turns out to be broken exactly when it is needed.
 *
 * @throws If any variable is missing or the wrong shape, naming each one.
 */
export function parseEnv(raw: unknown): Env {
  const parsed = schema.safeParse(raw)

  if (!parsed.success) {
    throw new Error(
      `Invalid environment configuration:\n${parsed.error.issues
        .map((issue) => `  ${issue.path.join('.')}: ${issue.message}`)
        .join('\n')}`,
    )
  }

  return {
    apiBaseUrl: parsed.data.VITE_API_BASE_URL,
    authBaseUrl: parsed.data.VITE_AUTH_BASE_URL,
    consentCookieDomain: parsed.data.VITE_CONSENT_COOKIE_DOMAIN || undefined,
    mode: parsed.data.MODE,
    isProduction: parsed.data.MODE === 'production',
  }
}

export const env: Env = parseEnv(import.meta.env)
